import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Drawer as MUIDrawer,
  Tooltip,
} from '@mui/material'
import clsx from 'clsx'

import { Link, routes, useLocation, useParams } from '@cedarjs/router'
import useHasFeature from 'src/hooks/useHasFeature'
import useIsAdmin from 'src/hooks/useIsAdmin'
import { isWingworkUser } from 'src/utils/permissions'
import NavIcon from '../components/NavIcon'

type DrawerListItem = {
  text: string
  icon: React.ReactElement
  disabled?: boolean
  to?: string
}

const ListGroup = ({
  title,
  items,
  expanded,
}: {
  title?: string
  items: DrawerListItem[]
  expanded: boolean
}) => {
  const { pathname } = useLocation()

  return (
    <List
      subheader={
        <ListSubheader
          className={`
              static overflow-hidden transition-all duration-300 ease-in-out
              ${expanded ? 'max-h-8 opacity-100' : 'max-h-0 opacity-0'}
              bg-transparent normal-case
            `}
        >
          {title?.toUpperCase() || ''}
        </ListSubheader>
      }
    >
      {items.map(({ text, icon, to, disabled }) => {
        const isActive = to && pathname.startsWith(to)

        return (
          <ListItem key={text} disablePadding>
            <Tooltip
              title={text}
              placement="right"
              slotProps={{
                // offset used to snug the tooltip to the button just right
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -7],
                      },
                    },
                  ],
                },
              }}
            >
              <ListItemButton
                disabled={!to || disabled}
                component={to && Link}
                to={to && to}
                className={`
                  min-h-6 px-[12px] py-[6px]
                  ${expanded ? 'justify-[initial]' : 'justify-center'}
                  ${
                    isActive
                      ? 'bg-blueGrey-50 hover:bg-blueGrey-100'
                      : 'hover:bg-blueGrey-50'
                  }
                  mx-1 rounded-lg
                `}
              >
                <ListItemIcon
                  className={`
                    min-w-0 justify-center
                    ${expanded ? 'mr-3' : 'mr-0'}
                  `}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={text}
                  className={`cursor-pointer
                    ${expanded ? 'opacity-100' : 'opacity-0'}
                  `}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )
      })}
    </List>
  )
}

const INTERNAL_PATH_NAMES = ['/admin', '/onboarding']

export const navbarExpandedWidth = 240
export const navbarMiniWidth = 56

const Drawer = ({ expanded = true }: { expanded?: boolean }) => {
  const { pathname } = useLocation()
  const pathParams = useParams()

  const isAdmin = useIsAdmin()
  const isInternalUser = isWingworkUser()
  const isAdminPage = React.useMemo(
    () => INTERNAL_PATH_NAMES.some((path) => pathname.startsWith(path)),
    [pathname]
  )
  const orgName = !isAdminPage ? pathParams.orgName : undefined
  const { hasFeature: inventoryEnabled } = useHasFeature('Inventory', orgName)
  const { hasFeature: hasFormsEnabled } = useHasFeature('Forms', orgName)
  const { hasFeature: orgHasComments } = useHasFeature('Comments', orgName)
  const { hasFeature: componentsEnabled } = useHasFeature(
    'Components Page',
    orgName
  )
  const { hasFeature: hasExternalWorkOrders } = useHasFeature(
    'External Work Orders',
    orgName
  )

  const sidebarNavigationGroups = React.useMemo(() => {
    if (isAdminPage) {
      return {
        Manage: [
          {
            text: 'Users',
            icon: <NavIcon variety="users" />,
            to: routes.administration({ component: 'users' }),
          },
          {
            text: 'Organizations',
            icon: <NavIcon variety="organizations" />,
            to: routes.adminOrganizations(),
          },
          {
            text: 'Aircraft',
            icon: <NavIcon variety="aircraft" />,
            to: routes.administration({ component: 'aircrafts' }),
          },
          {
            text: 'Onboarding',
            icon: <NavIcon variety="onboarding" />,
            to: routes.adminOnboarding(),
          },
          {
            text: 'Aircraft Templates',
            icon: <NavIcon variety="aircraftTemplates" />,
            to: routes.adminAircraftTemplates(),
          },
          {
            text: 'Self Onboarding',
            icon: <NavIcon variety="selfOnboarding" />,
            to: routes.adminSelfOnboarding(),
          },
          {
            text: 'Support',
            icon: <NavIcon variety="support" />,
            to: routes.adminSupport(),
          },
          {
            text: 'Features',
            icon: <NavIcon variety="features" />,
            to: routes.featureSettings(),
          },
          {
            text: 'Activity',
            icon: <NavIcon variety="activity" />,
            to: routes.administration({ component: 'activity' }),
          },
          {
            text: 'References',
            icon: <NavIcon variety="references" />,
            to: routes.adminReferences(),
          },
          {
            text: 'Integrations',
            icon: <NavIcon variety="integrations" />,
            to: routes.adminIntegrations(),
          },
          {
            text: 'Task Linking',
            icon: <NavIcon variety="taskLinking" />,
            to: routes.adminTaskLinking(),
          },
          {
            text: 'Monitoring',
            icon: <NavIcon variety="monitoring" />,
            to: routes.monitoringDashboard(),
          },
        ],
      }
    } else {
      return {
        Analytics: [
          {
            text: 'Dashboard',
            icon: <NavIcon variety="dashboard" />,
            to: routes.fleetDashboard({ orgName }),
          },
          {
            text: 'Reports',
            icon: <NavIcon variety="reports" />,
            to: routes.reports({ orgName }),
          },
          {
            text: 'Document Hub',
            icon: <NavIcon variety="documentHub" />,
            to: routes.documentHub({ orgName }),
          },
          ...(hasFormsEnabled
            ? [
                {
                  text: 'Forms',
                  icon: <NavIcon variety="forms" />,
                  to: routes.forms({ orgName }),
                },
              ]
            : []),
        ],
        Maintenance: [
          {
            text: 'Due List',
            icon: <NavIcon variety="dueList" />,
            to: routes.dueList({ orgName }),
          },
          {
            text: 'Maintenance Items',
            icon: <NavIcon variety="maintenanceItems" />,
            to: routes.maintenanceItems({ orgName: orgName }),
          },
          ...(componentsEnabled
            ? [
                {
                  text: 'Components',
                  icon: <NavIcon variety="components" />,
                  to: routes.componentsList({ orgName }),
                },
              ]
            : []),
          {
            text: 'Past Compliance',
            icon: <NavIcon variety="pastCompliance" />,
            to: routes.workCompleted({ orgName }),
          },
          {
            text: 'Logbook Entries',
            icon: <NavIcon variety="logbookEntries" />,
            to: routes.logbookEntriesLanding({ orgName }),
          },
          {
            text: 'Activity Log',
            icon: <NavIcon variety="activityLog" />,
            to: routes.activityLog({ orgName }),
          },
          {
            text: 'Purchase Orders',
            icon: <NavIcon variety="purchaseOrders" />,
            to: routes.purchaseOrders({ orgName }),
          },
          ...(isInternalUser
            ? [
                {
                  text: 'Logbook Ingestions',
                  icon: <NavIcon variety="logbookIngestions" />,
                  to: routes.logbookIngestions({ orgName }),
                },
              ]
            : []),
        ],
        Inventory: [
          {
            text: 'Inventory',
            icon: <NavIcon variety="inventory" />,
            to: routes.productsList({ orgName }),
            disabled: !inventoryEnabled,
          },
          {
            text: 'Due Inventory',
            icon: <NavIcon variety="dueInventory" />,
            to: routes.dueProducts({ orgName }),
            disabled: !inventoryEnabled,
          },
          {
            text: 'Tools',
            icon: <NavIcon variety="tools" />,
            to: routes.toolsList({ orgName }),
            disabled: !inventoryEnabled,
          },
        ],
        'Work Orders': [
          {
            text: 'My Work Orders',
            icon: <NavIcon variety="myWorkOrders" />,
            to: routes.workOrders({
              orgName,
              ordersDiscriminator: 'myorders',
            }),
          },
          {
            text: 'Scheduled',
            icon: <NavIcon variety="scheduled" />,
            to: routes.workOrders({
              orgName,
              ordersDiscriminator: 'scheduled',
            }),
          },
          {
            text: 'Completed',
            icon: <NavIcon variety="completed" />,
            to: routes.workOrders({
              orgName,
              ordersDiscriminator: 'completed',
            }),
          },
        ],
        ...(hasExternalWorkOrders
          ? {
              'External Work Orders': [
                {
                  text: 'Work Orders',
                  icon: <NavIcon variety="externalWorkOrders" />,
                  to: routes.externalWorkOrders({ orgName }),
                },
              ],
            }
          : {}),
      }
    }
  }, [pathParams.orgName, pathname])

  const expandedDrawerClasses =
    'transition-all ease-in duration-300 overflow-x-hidden'
  const miniDrawerClasses =
    'transition-all ease-out duration-300 overflow-x-hidden'

  return (
    <MUIDrawer
      variant="permanent"
      anchor={'left'}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar - 1,
        width: expanded ? navbarExpandedWidth : navbarMiniWidth,
        [`& .MuiDrawer-paper`]: {
          position: 'relative',
        },
        position: 'relative',
        height: 'calc(100vh - 64px)',
      }}
      className={clsx({
        ['box-sizing-border flex-shrink-0 whitespace-nowrap']: true,
        [expandedDrawerClasses]: expanded,
        [miniDrawerClasses]: !expanded,
      })}
      classes={{
        paper: clsx({
          [expandedDrawerClasses]: expanded,
          [miniDrawerClasses]: !expanded,
          'bg-transparent border-none': true,
        }),
      }}
    >
      {Object.keys(sidebarNavigationGroups).map((key) => {
        const items = sidebarNavigationGroups[key]
        return (
          <ListGroup key={key} title={key} items={items} expanded={expanded} />
        )
      })}

      <div style={{ flexGrow: 1 }} />
      <List>
        {!isAdminPage ? (
          <ListGroup
            items={[
              ...(orgHasComments
                ? [
                    {
                      text: 'Inbox',
                      icon: <NavIcon variety="inbox" />,
                      to: routes.inbox({ orgName }),
                    },
                  ]
                : []),
              ...(isAdmin
                ? [
                    {
                      text: 'Admin Settings',
                      icon: <NavIcon variety="adminSettings" />,
                      to: routes.administration({ component: 'users' }),
                    },
                  ]
                : []),
              {
                text: 'Settings',
                icon: <NavIcon variety="settings" />,
                to: routes.settings({ orgName }),
              },
            ]}
            expanded={expanded}
          />
        ) : (
          <ListGroup
            items={[
              {
                text: 'Back to App',
                icon: <NavIcon variety="backToApp" />,
                to: routes.landing(),
              },
            ]}
            expanded={expanded}
          />
        )}
      </List>
    </MUIDrawer>
  )
}

export default Drawer
